import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useCart } from "../../store/CartContext";
import { selectUser } from "../../store/slices/auth/slice";
import { createOrder } from "../../store/slices/orders/asyncThunk";
import { initiateRaiffeisenPayment } from "../../store/slices/payments/asyncThunk";
import { getProfile } from "../../store/slices/auth/asyncThunk";
import {
  selectCreateOrderLoading,
  selectOrdersErrors,
} from "../../store/slices/orders/slice";
import { toast } from "../../components/toast/sonner";

import {
  CheckoutWrapper,
  CheckoutContainer,
  CheckoutHeader,
  CheckoutTitle,
  CheckoutSubtitle,
  CheckoutContent,
  CheckoutForm,
  CheckoutSidebar,
  FormSection,
  SectionTitle,
  FormGroup,
  FormLabel,
  FormInput,
  FormSelect,
  FormTextarea,
  OrderSummary,
  SummaryTitle,
  OrderItems,
  OrderItem,
  ItemInfo,
  ItemTitle,
  ItemPrice,
  SummaryLabel,
  SummaryValue,
  TotalRow,
  CheckoutButton,
  BackButton,
  PaymentMethods,
  PaymentMethod,
  PaymentLabel,
  PaymentRadio,
  FormRow,
  ErrorMessage,
  SelectWrapper,
  ShippingLink,
  SummaryColumn,
  DeliveryContainer,
  SubtotalContainer,
  PaddingTop,
  QuantityBadge,
  CheckboxContainer,
  CheckboxInput,
  CheckboxLabel,
  TooltipWrapper,
  TooltipContent,
  PaymentAcceptIcon,
} from "./Checkout.styles";

import {
  FaArrowLeft,
  FaChevronDown,
  FaCreditCard,
  FaTruck,
  FaBuilding,
  FaFileInvoice,
  FaExternalLinkAlt,
  FaGift,
} from "react-icons/fa";
import { PiInfoBold } from "react-icons/pi";

import SmallClouds from "../../components/utils/SmallClouds";
import PaymentRedirect from "../../components/PaymentRedirect/PaymentRedirect";
import { formatPrice } from "../../utils/utils";
import { BASE_URL } from "../../api";

export default function Checkout() {
  console.log("Checkout komponenta učitana");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, clearCart, getTotal } = useCart();
  const user = useSelector(selectUser);
  const createOrderLoading = useSelector(selectCreateOrderLoading);
  const ordersErrors = useSelector(selectOrdersErrors);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    postalCode: "",
    country: "Srbija",
    paymentMethod: "card",
    cardNumber: "",
    expiryMonth: "",
    expiryYear: "",
    cvv: "",
    cardName: "",
    notes: "",
    isGift: false,
  });

  // Fetch fresh user data when component mounts and auto-fill form
  useEffect(() => {
    // Fetch fresh user profile data when navigating to checkout
    dispatch(getProfile())
      .unwrap()
      .then(() => {
        setUserDataLoaded(true);
      })
      .catch((err) => {
        console.log(err.message);
        setUserDataLoaded(true);
      });
    // eslint-disable-next-line
  }, []);

  // Auto-fill user info when user data is available
  useEffect(() => {
    if (user) {
      const updatedFormData = {
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phone: user.phone || "",
        address: user.address || "",
        city: user.city || "",
        postalCode: user.postalCode || "",
        country: "Srbija",
        paymentMethod: "card",
        cardNumber: "",
        expiryMonth: "",
        expiryYear: "",
        cvv: "",
        cardName: "",
        notes: "",
        isGift: false,
      };
      setFormData(updatedFormData);
    }
  }, [user]);

  const [errors, setErrors] = useState({});
  const [userDataLoaded, setUserDataLoaded] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  // Redirect if cart is empty with simple delay for cart to load
  useEffect(() => {
    const timer = setTimeout(() => {
      if (items.length === 0) {
        console.log("Cart je prazan, redirektuje na /setovi");
        navigate("/setovi");
      }
    }, 300); // Simple delay to allow cart to load from localStorage

    return () => clearTimeout(timer);
  }, [items, navigate]);

  // Handle orders errors
  useEffect(() => {
    if (ordersErrors.createOrder) {
      toast.error(ordersErrors.createOrder);
    }
  }, [ordersErrors]);

  const subtotal = getTotal();
  const shipping = formData.paymentMethod === "pickup" ? 0 : 490;
  // const shipping = formData.paymentMethod === "pickup" ? 0 : 0;
  const total = subtotal + shipping;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields - safer checking
    if (!formData.firstName || !formData.firstName.trim())
      newErrors.firstName = "Ime je obavezno";
    if (!formData.lastName || !formData.lastName.trim())
      newErrors.lastName = "Prezime je obavezno";
    if (!formData.email || !formData.email.trim())
      newErrors.email = "Email je obavezan";
    if (!formData.phone || !formData.phone.trim())
      newErrors.phone = "Telefon je obavezan";
    if (!formData.address || !formData.address.trim())
      newErrors.address = "Adresa je obavezna";
    if (!formData.city || !formData.city.trim())
      newErrors.city = "Grad je obavezan";
    if (!formData.postalCode || !formData.postalCode.trim())
      newErrors.postalCode = "Poštanski broj je obavezan";

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = "Unesite validan email";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Spreči dupli submit (brz dvoklik pre nego što se disabled stanje primeni)
    if (isProcessingPayment || createOrderLoading) {
      return;
    }

    // Wait for user data to be loaded before validating
    if (!userDataLoaded) {
      return;
    }

    if (!validateForm()) {
      console.log("Form validation failed");
      return;
    }

    // Check if user is logged in
    if (!user || !user.id) {
      toast.error("Morate biti ulogovani da biste izvršili kupovinu");
      // Ruta za prijavu je /prijava (ne /login); prosledi povratak na korpu
      navigate("/prijava?redirect=korpa");
      return;
    }

    console.log(
      "Form validation passed, payment method:",
      formData.paymentMethod,
    );

    try {
      setIsProcessingPayment(true);
      console.log("Starting order creation...");

      const orderData = {
        userId: user?.id, // Add user ID for backend
        items: items.map((item) => ({
          id: item.id,
          type: item.type,
          quantity: item.quantity,
          price: item.price,
          name: item.name || item.title,
          mainImage: item.mainImage || item.image,
        })),
        shippingInfo: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          postalCode: formData.postalCode,
          country: formData.country,
        },
        paymentMethod: formData.paymentMethod,
        subtotal,
        shipping,
        total,
        notes: formData.notes,
        isGift: formData.isGift,
      };

      // Handle different payment methods
      if (formData.paymentMethod === "card") {
        // For card payments, initiate Raiffeisen payment first
        console.log(
          "Initiating Raiffeisen payment with order data:",
          orderData,
        );

        try {
          const paymentResponse = await dispatch(
            initiateRaiffeisenPayment(orderData),
          ).unwrap();
          console.log("Raiffeisen response:", paymentResponse);

          // Backend now returns JSON with paymentData (like MMBeautySpot)
          if (paymentResponse.success && paymentResponse.data.paymentData) {
            setPaymentData(paymentResponse.data.paymentData);
            toast.success("Preusmeravanje na plaćanje...");
          } else {
            throw new Error(
              paymentResponse.message || "Greška pri iniciranju plaćanja",
            );
          }
        } catch (paymentError) {
          console.error("Greška u Raiffeisen payment:", paymentError);
          toast.error("Greška prilikom pokretanja plaćanja.");
          setErrors({
            general: "Greška prilikom pokretanja plaćanja. Pokušajte ponovo.",
          });
          setPaymentData(null);
          setIsProcessingPayment(false);
        }
      } else {
        // ✅ For non-card payments, create order immediately
        console.log(
          "Creating order for non-card payment:",
          formData.paymentMethod,
        );

        const orderResult = await dispatch(createOrder(orderData)).unwrap();
        console.log("Order created for non-card payment:", orderResult);

        toast.success("Narudžbina je uspešno kreirana!");

        // ✅ Clear cart immediately
        clearCart();

        // ✅ Determine order type for proper redirect
        const hasWorkshops = items.some((item) => item.type === "workshop");
        const hasPurchases = items.some(
          (item) => item.type === "purchase" || item.type === "rental",
        );

        let orderType = "purchase";
        if (hasWorkshops && hasPurchases) {
          orderType = "mixed";
        } else if (hasWorkshops) {
          orderType = "workshop";
        }

        console.log(`📦 Redirecting to success page with type: ${orderType}`);

        // ✅ Redirect to success page (same as card payment)

        navigate(
          `/placanje-uspesno?order=${orderResult.data.order.orderNumber}&type=${orderType}&method=${formData.paymentMethod}`,
        );
      }
    } catch (error) {
      console.error("Error during order creation:", error);
      toast.error(
        error.message || "Došlo je do greške prilikom procesiranja narudžbine.",
      );
      setErrors({
        general:
          error.message ||
          "Došlo je do greške prilikom procesiranja. Pokušajte ponovo.",
      });
      setIsProcessingPayment(false);
    }
  };

  const getItemPrice = (item) => {
    return item.price;
  };

  // If payment data received, show redirect component (like MMBeautySpot)
  if (paymentData) {
    return (
      <PaymentRedirect
        paymentData={paymentData}
        onError={(msg) => {
          setErrors({ general: msg });
          setPaymentData(null);
          setIsProcessingPayment(false);
        }}
      />
    );
  }

  return (
    <CheckoutWrapper>
      <SmallClouds experiences />

      <CheckoutContainer>
        <CheckoutHeader>
          <div>
            <CheckoutTitle>Finalizacija narudžbine</CheckoutTitle>
            <CheckoutSubtitle>
              Popunite podatke za dostavu i plaćanje
            </CheckoutSubtitle>
          </div>
          <BackButton onClick={() => navigate("/setovi")}>
            <FaArrowLeft />
            Nazad na setove
          </BackButton>
        </CheckoutHeader>

        <CheckoutContent>
          <CheckoutForm onSubmit={handleSubmit}>
            <FormSection>
              <SectionTitle>Lični podaci</SectionTitle>

              <FormRow>
                <FormGroup>
                  <FormLabel>Ime *</FormLabel>
                  <FormInput
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    $error={!!errors.firstName}
                  />
                  {errors.firstName && (
                    <ErrorMessage>{errors.firstName}</ErrorMessage>
                  )}
                </FormGroup>

                <FormGroup>
                  <FormLabel>Prezime *</FormLabel>
                  <FormInput
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    $error={!!errors.lastName}
                  />
                  {errors.lastName && (
                    <ErrorMessage>{errors.lastName}</ErrorMessage>
                  )}
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <FormLabel>Email *</FormLabel>
                  <FormInput
                    disabled
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    $error={!!errors.email}
                  />
                  {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <FormLabel>Telefon *</FormLabel>
                  <FormInput
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    $error={!!errors.phone}
                  />
                  {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
                </FormGroup>
              </FormRow>
            </FormSection>

            <FormSection>
              <SectionTitle>Adresa</SectionTitle>

              <FormGroup style={{ marginBottom: "1rem" }}>
                <FormLabel>Adresa *</FormLabel>
                <FormInput
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  $error={!!errors.address}
                />
                {errors.address && (
                  <ErrorMessage>{errors.address}</ErrorMessage>
                )}
              </FormGroup>

              <FormRow style={{ marginBottom: 0 }}>
                <FormGroup>
                  <FormLabel>Grad *</FormLabel>
                  <FormInput
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    $error={!!errors.city}
                  />
                  {errors.city && <ErrorMessage>{errors.city}</ErrorMessage>}
                </FormGroup>

                <FormGroup>
                  <FormLabel>Poštanski broj *</FormLabel>
                  <FormInput
                    type="text"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleInputChange}
                    $error={!!errors.postalCode}
                  />
                  {errors.postalCode && (
                    <ErrorMessage>{errors.postalCode}</ErrorMessage>
                  )}
                </FormGroup>

                <FormGroup>
                  <FormLabel>Zemlja</FormLabel>
                  <SelectWrapper>
                    <FormSelect
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                    >
                      <option value="Srbija">Srbija</option>
                      <option value="Bosna i Hercegovina">
                        Bosna i Hercegovina
                      </option>
                      <option value="Crna Gora">Crna Gora</option>
                      <option value="Makedonija">Makedonija</option>
                      <option value="Hrvatska">Hrvatska</option>
                      <option value="Slovenija">Slovenija</option>
                    </FormSelect>
                    <FaChevronDown />
                  </SelectWrapper>
                </FormGroup>
              </FormRow>
            </FormSection>

            <FormSection>
              <SectionTitle>Način plaćanja</SectionTitle>

              <PaymentMethods>
                <PaymentMethod
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, paymentMethod: "card" }))
                  }
                >
                  <PaymentRadio
                    type="radio"
                    name="paymentMethod"
                    value="card"
                    checked={formData.paymentMethod === "card"}
                    onChange={handleInputChange}
                  />
                  <PaymentLabel>
                    <FaCreditCard />
                    Kreditna/debitna kartica
                  </PaymentLabel>
                </PaymentMethod>

                {formData.country === "Srbija" && (
                  <PaymentMethod
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        paymentMethod: "bank",
                      }))
                    }
                  >
                    <PaymentRadio
                      type="radio"
                      name="paymentMethod"
                      value="bank"
                      checked={formData.paymentMethod === "bank"}
                      onChange={handleInputChange}
                    />
                    <PaymentLabel>
                      <FaFileInvoice />
                      Plaćanje uplatnicom
                    </PaymentLabel>
                    <ShippingLink
                      href={`${BASE_URL}/uploads/uplatnica.pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        position: "absolute",
                        fontSize: "1.2rem",
                        right: "1.5rem",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      Uplatnica
                      <FaExternalLinkAlt width={10} height={10} />
                    </ShippingLink>
                  </PaymentMethod>
                )}

                {formData.country === "Srbija" && (
                  <PaymentMethod
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        paymentMethod: "cash",
                      }))
                    }
                  >
                    <PaymentRadio
                      type="radio"
                      name="paymentMethod"
                      value="cash"
                      checked={formData.paymentMethod === "cash"}
                      onChange={handleInputChange}
                    />
                    <PaymentLabel>
                      <FaTruck />
                      Plaćanje pouzećem
                    </PaymentLabel>
                  </PaymentMethod>
                )}

                {formData.country === "Srbija" && (
                  <PaymentMethod
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        paymentMethod: "pickup",
                      }))
                    }
                  >
                    <PaymentRadio
                      type="radio"
                      name="paymentMethod"
                      value="pickup"
                      checked={formData.paymentMethod === "pickup"}
                      onChange={handleInputChange}
                    />
                    <PaymentLabel>
                      <FaBuilding />
                      Lično preuzimanje u vrtiću "Dobra Vila"
                    </PaymentLabel>
                  </PaymentMethod>
                )}
              </PaymentMethods>
            </FormSection>

            <FormSection>
              <SectionTitle>Dodatne napomene</SectionTitle>
              <FormGroup>
                <FormLabel>Napomene (opciono)</FormLabel>
                <FormTextarea
                  name="notes"
                  rows="4"
                  placeholder="Dodatne napomene za vašu narudžbinu..."
                  value={formData.notes}
                  onChange={handleInputChange}
                />
              </FormGroup>
            </FormSection>

            {errors.general && <ErrorMessage>{errors.general}</ErrorMessage>}
          </CheckoutForm>

          <CheckoutSidebar>
            <OrderSummary>
              <SummaryTitle>Pregled narudžbine</SummaryTitle>

              <OrderItems>
                {items.map((item) => (
                  <OrderItem key={`${item.id}-${item.type}`}>
                    <ItemInfo>
                      <ItemTitle>{item.name}</ItemTitle>

                      <ItemPrice>
                        {formatPrice(getItemPrice(item) * item.quantity)}
                      </ItemPrice>
                    </ItemInfo>

                    <QuantityBadge>{item.quantity}×</QuantityBadge>
                  </OrderItem>
                ))}
              </OrderItems>

              <PaddingTop>
                <SubtotalContainer>
                  <SummaryLabel>Međuzbir:</SummaryLabel>
                  <SummaryValue>{formatPrice(subtotal)}</SummaryValue>
                </SubtotalContainer>
                {formData.paymentMethod === "pickup" ? (
                  <DeliveryContainer>
                    <SummaryLabel>Lično preuzimanje</SummaryLabel>
                  </DeliveryContainer>
                ) : (
                  <DeliveryContainer>
                    <SummaryColumn>
                      <SummaryLabel>Dostava (Post Express):</SummaryLabel>
                      <ShippingLink
                        href="http://www.postexpress.rs/struktura/lat/cenovnik/cenovnik-unutrasnji-saobracaj.asp"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Cenovnik dostave
                      </ShippingLink>
                    </SummaryColumn>
                    <SummaryValue>≈ {formatPrice(shipping)}</SummaryValue>
                  </DeliveryContainer>
                )}
              </PaddingTop>

              <TotalRow>
                <SummaryLabel>Ukupno za plaćanje:</SummaryLabel>
                <SummaryValue>{formatPrice(total)} RSD</SummaryValue>
              </TotalRow>

              <CheckboxContainer>
                <CheckboxInput
                  type="checkbox"
                  id="isGift"
                  name="isGift"
                  checked={formData.isGift}
                  onChange={handleCheckboxChange}
                />
                <CheckboxLabel htmlFor="isGift">
                  <FaGift size={14} />
                  Kupujem kao poklon
                  <TooltipWrapper
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    onClick={() => setShowTooltip(!showTooltip)}
                  >
                    <PiInfoBold size={18} />
                    <TooltipContent $show={showTooltip}>
                      Setovi će biti vidljivi na nalogu primaoca poklona, ne na
                      vašem. Za primaoca će biti kreiran nalog, a pristupni
                      podaci biće mu dostavljeni zajedno sa setom.
                    </TooltipContent>
                  </TooltipWrapper>
                </CheckboxLabel>
              </CheckboxContainer>

              <CheckoutButton
                type="submit"
                form="checkout-form"
                disabled={createOrderLoading || isProcessingPayment}
                onClick={handleSubmit}
              >
                {createOrderLoading
                  ? "Kreiranje narudžbine..."
                  : isProcessingPayment
                    ? "Pokretanje plaćanja..."
                    : "Završi narudžbinu"}
              </CheckoutButton>
              <PaymentAcceptIcon />
            </OrderSummary>
          </CheckoutSidebar>
        </CheckoutContent>
      </CheckoutContainer>
    </CheckoutWrapper>
  );
}
